import { useMemo, useRef } from 'react';

import { useDimensions } from '../../../../../../../utils/hooks';
import { PublishedOption } from '../types';

export interface UsePieChart {
  publishedOptions: PublishedOption[];
  canSeeResults: boolean;
}

const usePieChart = ({ publishedOptions, canSeeResults }: UsePieChart) => {
  const rootRef = useRef<HTMLDivElement>(null);

  const dimensions = useDimensions(rootRef);
  const isResultVisibleRef = useRef(false);

  const radius = Math.min(dimensions.width, dimensions.height) / 2;

  const pie = useMemo(() => {
    // Compute a pie generator = a function that transforms a dataset in a list of arcs
    // D3 sorts data by default. To stop sorting, we have to call sort with null
    const pieGenerator = d3
      .pie<PublishedOption>()
      .value((d) => d.totalSelections)
      .sort(null);

    // When there is no data to draw pie chart, draw a pie chart with equal shares
    if (!canSeeResults || publishedOptions.every((item) => item.totalSelections === 0)) {
      isResultVisibleRef.current = false;
      return pieGenerator(publishedOptions.map((item) => ({ ...item, totalSelections: 1 })));
    }

    isResultVisibleRef.current = true;

    // Use this pie generator on our initial dataset
    return pieGenerator(publishedOptions);
  }, [publishedOptions, canSeeResults]);

  return { rootRef, dimensions, pie, radius, isResultVisible: isResultVisibleRef.current };
};

export default usePieChart;
